import dayjs from 'dayjs';
import moment from 'moment';
import { supabase } from '../../supabaseClient';
import { fetchAllRows } from './supabaseFetchAll';
import { MONTHS_SHORT } from './financialPeriod';

// Transforma dados da OS (BudgetServiceOrder) para o formato de Relatórios.
// Movido de ReportsContainer para virar fonte única de verdade (relatório + dashboard).
export const transformOSToReportData = (osData, paintServiceIds = []) => {
  return osData.map((os) => {
    let services = [];
    try {
      services = os.service ? JSON.parse(os.service) : [];
    } catch (e) {
      console.error('Erro ao parsear services:', e);
    }

    let aeServices = [];
    try {
      aeServices = os.ae_service ? JSON.parse(os.ae_service) : [];
    } catch (e) {
      console.error('Erro ao parsear ae_service:', e);
    }

    let parts = [];
    try {
      parts = os.parts ? JSON.parse(os.parts) : [];
    } catch (e) {
      console.error('Erro ao parsear parts:', e);
    }

    let mo_lataria = 0;
    let qtd_horas_lataria = 0;
    let qtd_pecas_pintadas = 0;

    services.forEach((service) => {
      if (service.check) {
        const value = parseFloat(service.value) || 0;
        mo_lataria += value;

        if (service.time) {
          const timeParts = service.time.split(':');
          const hours = parseInt(timeParts[0] || 0);
          const minutes = parseInt(timeParts[1] || 0);
          qtd_horas_lataria += hours + minutes / 60;
        }

        if (paintServiceIds.includes(service.service_type)) {
          qtd_pecas_pintadas++;
        }
      }
    });

    let mo_estetica = 0;
    let qtd_horas_estetica = 0;

    aeServices.forEach((aeService) => {
      if (aeService.check) {
        const value = parseFloat(aeService.value) || 0;
        mo_estetica += value;

        if (aeService.services && Array.isArray(aeService.services) && aeService.services.length > 0) {
          const comboQuantity = parseFloat(aeService.quantity) || 1;
          aeService.services.forEach((service) => {
            const serviceQuantity = parseFloat(service.quantity) || 1;
            qtd_horas_estetica += serviceQuantity * comboQuantity;
          });
        } else {
          const quantity = parseFloat(aeService.quantity) || 1;
          qtd_horas_estetica += quantity;
        }
      }
    });

    let valor_pecas = 0;
    let custo_pecas = 0;

    parts.forEach((part) => {
      if (part.check) {
        const quantity = parseFloat(part.quantity) || 0;
        const finalValue = parseFloat(part.final_value) || 0;
        const grossPrice = parseFloat(part.gross_price) || 0;

        valor_pecas += finalValue > 0 ? finalValue * quantity : grossPrice;

        const unitaryValue = parseFloat(part.unitaryValue) || 0;
        custo_pecas += unitaryValue * quantity;
      }
    });

    const bodyshop_discount = parseFloat(os.bodyshop_discount) || 0;
    const aesthetics_discount = parseFloat(os.aesthetics_discount) || 0;
    const parts_discount = parseFloat(os.parts_discount) || 0;

    const mo_lataria_final = Math.max(0, mo_lataria - bodyshop_discount);
    const mo_estetica_final = Math.max(0, mo_estetica - aesthetics_discount);
    const valor_pecas_final = Math.max(0, valor_pecas - parts_discount);

    const lucro_pecas = valor_pecas_final - custo_pecas;
    const total_os = mo_lataria_final + mo_estetica_final + valor_pecas_final;

    const statusMap = { 1: 'Aberta', 2: 'Fechada', 3: 'Quitada' };
    const finalDate = os.close_date || os.delivery_date;

    return {
      id: os.id,
      code: os.code || '',
      vehicle: os.Vehicle
        ? `${os.Vehicle.model || ''} - ${os.Vehicle.plate || ''}`
        : '',
      mo_lataria: parseFloat(mo_lataria_final.toFixed(2)),
      qtd_horas_lataria: parseFloat(qtd_horas_lataria.toFixed(2)),
      qtd_pecas_pintadas,
      mo_estetica: parseFloat(mo_estetica_final.toFixed(2)),
      qtd_horas_estetica: parseFloat(qtd_horas_estetica.toFixed(2)),
      valor_pecas: parseFloat(valor_pecas_final.toFixed(2)),
      custo_pecas: parseFloat(custo_pecas.toFixed(2)),
      lucro_pecas: parseFloat(lucro_pecas.toFixed(2)),
      total_os: parseFloat(total_os.toFixed(2)),
      status: statusMap[os.status] || 'Aberta',
      previsao_entrega: finalDate ? dayjs(finalDate).format('DD/MM/YYYY') : '',
      created_at: os.created_at ? dayjs(os.created_at).format('DD/MM/YYYY') : '',
    };
  });
};

// Replica exatamente o filtro de data do relatório (ReportsContainer.doFilter):
// compara previsao_entrega (DD/MM/YYYY) com o intervalo, bordas inclusivas.
// `from`/`to` aceitam Date (relatório) ou ISO 'YYYY-MM-DD' (dashboard).
export const isOSInDateRange = (item, from, to) => {
  if (!from || !to) return true;
  const startDate = moment(from).startOf('day');
  const endDate = moment(to).endOf('day');
  const itemDate = moment(item.previsao_entrega, 'DD/MM/YYYY');
  return itemDate.isBetween(startDate, endDate, null, '[]');
};

export const filterOSByDateRange = (rows, from, to) =>
  rows.filter((item) => isOSInDateRange(item, from, to));

// Verbatim de ReportsScreen.calculateTotals — soma de cada coluna.
export const calculateReportTotals = (rows) =>
  rows.reduce(
    (acc, item) => ({
      mo_lataria: acc.mo_lataria + item.mo_lataria,
      qtd_horas_lataria: acc.qtd_horas_lataria + item.qtd_horas_lataria,
      qtd_pecas_pintadas: acc.qtd_pecas_pintadas + item.qtd_pecas_pintadas,
      mo_estetica: acc.mo_estetica + item.mo_estetica,
      qtd_horas_estetica: acc.qtd_horas_estetica + item.qtd_horas_estetica,
      valor_pecas: acc.valor_pecas + item.valor_pecas,
      custo_pecas: acc.custo_pecas + item.custo_pecas,
      lucro_pecas: acc.lucro_pecas + item.lucro_pecas,
      total_os: acc.total_os + item.total_os,
    }),
    {
      mo_lataria: 0, qtd_horas_lataria: 0, qtd_pecas_pintadas: 0,
      mo_estetica: 0, qtd_horas_estetica: 0,
      valor_pecas: 0, custo_pecas: 0, lucro_pecas: 0, total_os: 0,
    }
  );

// Verbatim de ReportsScreen.calculateCounts — OS por tipo (valor > 0) e total.
export const calculateReportCounts = (rows) => ({
  qtd_os_funilaria: rows.filter((item) => item.mo_lataria > 0).length,
  qtd_os_servicos: rows.filter((item) => item.mo_estetica > 0).length,
  qtd_os_pecas: rows.filter((item) => item.valor_pecas > 0).length,
  qtd_os_total: rows.length,
});

// Verbatim de ReportsTableFooter — ticket médio por seção.
export const calculateTicketMedio = (totals, counts) => ({
  mo_lataria: counts.qtd_os_funilaria > 0 ? totals.mo_lataria / counts.qtd_os_funilaria : 0,
  qtd_horas_lataria: totals.qtd_horas_lataria > 0 ? totals.mo_lataria / totals.qtd_horas_lataria : 0,
  qtd_pecas_pintadas: totals.qtd_pecas_pintadas > 0 ? totals.mo_lataria / totals.qtd_pecas_pintadas : 0,
  mo_estetica: counts.qtd_os_servicos > 0 ? totals.mo_estetica / counts.qtd_os_servicos : 0,
  qtd_horas_estetica: totals.qtd_horas_estetica > 0 ? totals.mo_estetica / totals.qtd_horas_estetica : 0,
  valor_pecas: counts.qtd_os_pecas > 0 ? totals.valor_pecas / counts.qtd_os_pecas : 0,
  total_os: counts.qtd_os_total > 0 ? totals.total_os / counts.qtd_os_total : 0,
});

// Verbatim de ReportsTableFooter — % sobre a receita total.
// A chave `qtd_horas_lataria` é o share da FUNILARIA (mantido igual ao rodapé).
export const calculatePercentualReceita = (totals) => ({
  qtd_horas_lataria: totals.total_os > 0 ? (totals.mo_lataria / totals.total_os) * 100 : 0,
  mo_estetica: totals.total_os > 0 ? (totals.mo_estetica / totals.total_os) * 100 : 0,
  valor_pecas: totals.total_os > 0 ? (totals.valor_pecas / totals.total_os) * 100 : 0,
});

// Bucketiza o faturamento das OS (total_os) por mês de previsao_entrega, nos
// últimos N meses até referenceDate. Pura; alinhada por índice com
// bucketEntriesByMonth (mesmo referenceDate/monthsCount) para o gráfico de linha.
export const bucketOSByMonth = (osRows, referenceDate, monthsCount = 6) => {
  const buckets = [];
  for (let i = monthsCount - 1; i >= 0; i -= 1) {
    const d = new Date(referenceDate.getFullYear(), referenceDate.getMonth() - i, 1);
    buckets.push({ mes: MONTHS_SHORT[d.getMonth()], year: d.getFullYear(), month: d.getMonth(), faturamento: 0, os: 0 });
  }
  (osRows || []).forEach((os) => {
    const p = os.previsao_entrega; // DD/MM/YYYY
    if (!p || p.length !== 10) return;
    const month = Number(p.slice(3, 5)) - 1;
    const year = Number(p.slice(6, 10));
    const bucket = buckets.find((b) => b.year === year && b.month === month);
    if (bucket) {
      bucket.faturamento += Number(os.total_os) || 0;
      bucket.os += 1;
    }
  });
  return buckets.map(({ mes, faturamento, os }) => ({ mes, faturamento, os }));
};

// IDs dos ServiceTypes de pintura da empresa (para contar peças pintadas).
export const loadPaintServiceIds = async (companyId) => {
  const { data } = await supabase
    .from('ServiceTypes')
    .select('id')
    .eq('id_company', companyId)
    .ilike('title', '%PINTURA%')
    .is('deleted_at', null);
  return data?.map((st) => st.id) || [];
};

// Sem client_signature/checklists: a OS guarda a assinatura em base64.
const OS_REPORT_COLUMNS = `
  id, code, status, service, ae_service, parts,
  bodyshop_discount, aesthetics_discount, parts_discount,
  close_date, delivery_date, created_at,
  Vehicle(model, plate)
`;

// Carrega TODAS as OS da empresa (paginado) e já transforma no formato do
// relatório. Ordena por created_at desc + id (desempate determinístico exigido
// por fetchAllRows), preservando a ordem padrão do relatório.
export const loadOSForReport = async (companyId) => {
  if (!companyId) return [];
  const paintServiceIds = await loadPaintServiceIds(companyId);
  const { data, error } = await fetchAllRows(() =>
    supabase
      .from('BudgetServiceOrder')
      .select(OS_REPORT_COLUMNS)
      .eq('id_company', companyId)
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
      .order('id', { ascending: true })
  );
  if (error) {
    console.error('Erro ao carregar OS para relatório:', error);
    return [];
  }
  if (!data) return [];
  return transformOSToReportData(data, paintServiceIds);
};
